import { FirebaseDataProvider } from 'react-admin-firebase';
import {
    CreateParams,
    DataProvider,
    GetListParams,
    GetOneParams,
    Identifier,
    UpdateParams,
    withLifecycleCallbacks,
} from 'react-admin';
import {
    Transaction,
    Fund,
    Contact,
    ContactNote,
    Deal,
    DealNote,
    RAFile,
    User,
    UsersFormData,
    NewsletterSubscription,
    InviteUserPayload,
    UpdateUserPatch,
    AdminInviteResult,
    AdminResendInviteResult,
    MyApiKey,
} from '../../types/types';
import { GateAccountOption, UnregisteredGateAccount } from '../../types/gates';
import type {
    LegalDecision,
    RecordLegalDecisionsResult,
} from '../../legal/legalState';
import { v4 as uuidv4 } from 'uuid';
import { getActivityLog } from '../commons/activity';
import { getCompanyAvatar } from '../commons/getCompanyAvatar';
import { getContactAvatar } from '../commons/getContactAvatar';
import { firebase, firebaseConfig } from './firebase';
import {
    doc,
    updateDoc,
    getDoc,
    getFirestore,
    where,
    limit,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { customAlphabet } from 'nanoid';

import {
    collection,
    query,
    getDocs,
    orderBy,
    Firestore,
} from 'firebase/firestore';

// Generate a short, readable ID (15 characters)
const generateTransactionId = () => {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    return (
        customAlphabet(alphabet, 5)() +
        '-' +
        customAlphabet(alphabet, 5)() +
        '-' +
        customAlphabet(alphabet, 5)()
    );
};

if (!firebaseConfig.projectId) {
    throw new Error(
        'Please set the Firebase project ID in environment variables.'
    );
}

const options = {
    logging: false,
    lazyLoading: {
        enabled: true,
    },
};

// Initialize Firebase Data Provider
const baseDataProvider = FirebaseDataProvider(firebaseConfig, options);

// Process company logo upload
const processCompanyLogo = async (params: any) => {
    let logo = params.data.logo;

    if (typeof logo !== 'object' || logo === null || !logo.src) {
        logo = await getCompanyAvatar(params.data);
    } else if (logo.rawFile instanceof File) {
        await uploadToBucket(logo);
    }

    return {
        ...params,
        data: {
            ...params.data,
            logo,
        },
    };
};

// Process contact avatar upload
async function processContactAvatar(
    params: CreateParams<Contact> | UpdateParams<Contact>
) {
    const { data } = params;
    if (data.avatar?.src || !data.email_jsonb || !data.email_jsonb.length) {
        return params;
    }
    const avatarUrl = await getContactAvatar(data);

    return {
        ...params,
        data: {
            ...data,
            avatar: { src: avatarUrl || undefined },
        },
    };
}

// Extend Firebase Data Provider with Custom Methods
const dataProviderWithCustomMethods = {
    ...baseDataProvider,

    async subscribeNewsletter({
        email,
        first_name,
        last_name,
    }: NewsletterSubscription) {
        const db = getFirestore();
        const {
            addDoc,
            collection: col,
            serverTimestamp: ts,
        } = await import('firebase/firestore');
        const payload: Record<string, any> = {
            email: String(email).trim().toLowerCase(),
            created_at: ts(),
        };
        if (first_name) payload.first_name = first_name;
        if (last_name) payload.last_name = last_name;
        const ref = await addDoc(col(db, 'newsletter_subscribers'), payload);
        return { id: ref.id };
    },

    // Override the getList method to use getListUsers for users resource
    async getList(resource: string, params: GetListParams) {
        // Check if we should use the custom users list function
        if (resource === 'users') {
            return this.getListUsers(params);
        }

        // Otherwise use the default implementation
        return baseDataProvider.getList(resource, params);
    },

    async getListUsers(params: GetListParams) {
        const modifiedParams = {
            ...params,
            filter: { ...(params.filter || {}) },
        };
        const searchTerm = modifiedParams.filter?.q?.toLowerCase();
        if (searchTerm) {
            delete modifiedParams.filter.q;
        }

        // react-admin-firebase translates a single-key non-numeric filter
        // into a prefix-search range query (>= value AND < value+'z'),
        // which Firestore rejects when orderBy is on a different field.
        // Run a direct equality query when status is the filter (used by
        // /admin/invites with sort on invite_sent_at).
        const statusFilter = modifiedParams.filter?.status;
        if (typeof statusFilter === 'string') {
            return this.getListUsersByStatus(
                statusFilter,
                modifiedParams,
                searchTerm
            );
        }

        // Firestore orderBy() silently drops docs missing the sort field —
        // sort in memory so invited/legacy users always appear.
        return this.getListUsersInMemory(modifiedParams, searchTerm);
    },

    async getListUsersInMemory(
        params: GetListParams,
        searchTerm: string | undefined
    ) {
        const db = getFirestore();
        const snap = await getDocs(collection(db, 'users'));
        let data: any[] = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        if (searchTerm) {
            const fields = [
                'first_name',
                'last_name',
                'email',
                'company',
                'referred_by',
                'role',
                'status',
            ];
            data = data.filter(u =>
                fields.some(
                    f => u[f] && String(u[f]).toLowerCase().includes(searchTerm)
                )
            );
        }

        if (params.sort?.field) {
            const { field, order } = params.sort;
            const dir = order === 'DESC' ? -1 : 1;
            data.sort((a, b) => {
                const av = a[field]?.toMillis?.() ?? a[field];
                const bv = b[field]?.toMillis?.() ?? b[field];
                if (av == null && bv == null) return 0;
                if (av == null) return -dir;
                if (bv == null) return dir;
                if (av < bv) return -dir;
                if (av > bv) return dir;
                return 0;
            });
        }

        const total = data.length;
        const { page = 1, perPage = 10 } = params.pagination ?? {};
        data = data.slice((page - 1) * perPage, page * perPage);

        return { data, total };
    },

    async getListUsersByStatus(
        status: string,
        params: GetListParams,
        searchTerm: string | undefined
    ) {
        const db = getFirestore();
        const snap = await getDocs(
            query(collection(db, 'users'), where('status', '==', status))
        );
        let data: any[] = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        if (searchTerm) {
            const fields = [
                'first_name',
                'last_name',
                'email',
                'company',
                'referred_by',
                'role',
                'status',
            ];
            data = data.filter(u =>
                fields.some(
                    f => u[f] && String(u[f]).toLowerCase().includes(searchTerm)
                )
            );
        }

        if (params.sort?.field) {
            const { field, order } = params.sort;
            const dir = order === 'DESC' ? -1 : 1;
            data.sort((a, b) => {
                const av = a[field]?.toMillis?.() ?? a[field];
                const bv = b[field]?.toMillis?.() ?? b[field];
                if (av == null && bv == null) return 0;
                if (av == null) return -dir;
                if (bv == null) return dir;
                if (av < bv) return -dir;
                if (av > bv) return dir;
                return 0;
            });
        }

        const total = data.length;
        const { page = 1, perPage = 10 } = params.pagination ?? {};
        data = data.slice((page - 1) * perPage, page * perPage);

        return { data, total };
    },

    async getListTimeseries(resource: string, params: GetListParams) {
        const modifiedParams = { ...params };

        // Extract and remove timestamp filters to avoid confusion on server side
        const timestampGte = modifiedParams.filter?.timestamp_gte;
        const timestampLte = modifiedParams.filter?.timestamp_lte;
        if (timestampGte) delete modifiedParams.filter.timestamp_gte;
        if (timestampLte) delete modifiedParams.filter.timestamp_lte;

        // Authenticated request
        const result = await baseDataProvider.getList(resource, modifiedParams);

        // Client-side filtering
        const filteredData = result.data.filter((doc: any) => {
            const ts =
                doc.timestamp instanceof Date
                    ? doc.timestamp
                    : new Date(doc.timestamp);
            if (timestampGte && ts < timestampGte) return false;
            if (timestampLte && ts > timestampLte) return false;
            return true;
        });

        return {
            data: filteredData,
            total: filteredData.length,
        };
    },

    async createUser(body: UsersFormData) {
        const db = firebase.firestore();
        const docRef = await db.collection('users').add(body);
        const doc = await docRef.get();

        if (!doc.exists) throw new Error('Failed to create account manager');

        return doc.data();
    },

    // Option 1: If you have access to the FireClient instance
    async updatePassword(id: Identifier) {
        const user = firebase.auth().currentUser;
        if (!user) throw new Error('No user found');

        await user.updatePassword('newStrongPassword123');
        return true;
    },

    async updateUser(
        id: Identifier,
        data: Partial<Omit<UsersFormData, 'password'>>
    ) {
        // Use the baseDataProvider's update method to handle all FireClient processing
        const result = await baseDataProvider.update('users', {
            id,
            data,
            previousData: {}, // You can provide the previous data if available
        });

        return result;
    },

    async updateFund(id: Identifier, data: Partial<Fund>) {
        // Use the baseDataProvider's update method to handle all FireClient processing
        const result = await baseDataProvider.update('funds', {
            id,
            data,
            previousData: {}, // You can provide the previous data if available
        });

        return result;
    },

    async createTransaction(data: Partial<Transaction>) {
        const newId = generateTransactionId(); // Example: "8JT8M-82HLR-SNWQX"

        const transactionData = {
            ...data,
            id: newId,
            transaction_id: newId,
            timestamp: new Date().getTime(),
        };

        // call and return the newly created transaction data
        const createdTransaction = await baseDataProvider.create(
            'transactions',
            { data: transactionData }
        );
        return createdTransaction.data;
    },

    async unarchiveDeal(deal: Deal) {
        const { data: deals } = await baseDataProvider.getList<Deal>('deals', {
            filter: { stage: deal.stage },
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'index', order: 'ASC' },
        });

        const updatedDeals = deals.map((d, index) => ({
            ...d,
            index: d.id === deal.id ? 0 : index + 1,
            archived_at: d.id === deal.id ? null : d.archived_at,
        }));

        return await Promise.all(
            updatedDeals.map(updatedDeal =>
                baseDataProvider.update('deals', {
                    id: updatedDeal.id,
                    data: updatedDeal,
                    previousData: deals.find(d => d.id === updatedDeal.id),
                })
            )
        );
    },

    async adminInviteUser(
        payload: InviteUserPayload
    ): Promise<AdminInviteResult> {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<InviteUserPayload, AdminInviteResult>(
            fns,
            'adminInviteUser'
        );
        const res = await fn(payload);
        return res.data;
    },

    async adminResendInvite(uid: Identifier): Promise<AdminResendInviteResult> {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<{ uid: string }, AdminResendInviteResult>(
            fns,
            'adminResendInvite'
        );
        const res = await fn({ uid: String(uid) });
        return res.data;
    },

    async adminRevokeInvite(uid: Identifier) {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<{ uid: string }, { ok: true }>(
            fns,
            'adminRevokeInvite'
        );
        const res = await fn({ uid: String(uid) });
        return res.data;
    },

    async adminUpdateUser(uid: Identifier, patch: UpdateUserPatch) {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<
            { uid: string; patch: UpdateUserPatch },
            { ok: true }
        >(fns, 'adminUpdateUser');
        const res = await fn({ uid: String(uid), patch });
        return res.data;
    },

    async adminCreateApiKey(args: {
        uid: string;
        key_hash: string;
        prefix: string;
        label: string;
    }) {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<typeof args, { ok: true }>(
            fns,
            'adminCreateApiKey'
        );
        const res = await fn(args);
        return res.data;
    },

    async adminRevokeApiKey(keyHash: string) {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<
            { key_hash: string },
            { ok: true; already_revoked: boolean }
        >(fns, 'adminRevokeApiKey');
        const res = await fn({ key_hash: keyHash });
        return res.data;
    },

    async listMyApiKeys(): Promise<{ keys: MyApiKey[] }> {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<Record<string, never>, { keys: MyApiKey[] }>(
            fns,
            'listMyApiKeys'
        );
        const res = await fn({});
        return { keys: res.data?.keys ?? [] };
    },

    async createMyApiKey(args: {
        key_hash: string;
        prefix: string;
        label: string;
    }) {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<typeof args, { ok: true }>(
            fns,
            'createMyApiKey'
        );
        const res = await fn(args);
        return res.data;
    },

    async revokeMyApiKey(keyHash: string) {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<
            { key_hash: string },
            { ok: true; already_revoked: boolean }
        >(fns, 'revokeMyApiKey');
        const res = await fn({ key_hash: keyHash });
        return res.data;
    },

    async adminSetGateOperators(uid: Identifier, accountIds: string[]) {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<
            { uid: string; account_ids: string[] },
            { ok: true }
        >(fns, 'adminSetGateOperators');
        const res = await fn({ uid: String(uid), account_ids: accountIds });
        return res.data;
    },

    async adminListAccounts(): Promise<{
        accounts: GateAccountOption[];
        unregistered: UnregisteredGateAccount[];
    }> {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<
            Record<string, never>,
            {
                accounts: GateAccountOption[];
                unregistered?: UnregisteredGateAccount[];
            }
        >(fns, 'adminListAccounts');
        const res = await fn({});
        // Defensive: a mid-deploy skew (hosting ships before functions in the
        // same CI run) or any response-shape drift must degrade to empty
        // lists, not crash the caller with undefined.
        return {
            accounts: res.data?.accounts ?? [],
            unregistered: res.data?.unregistered ?? [],
        };
    },

    async adminAddAccount(accountId: string, name?: string) {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<
            { account_id: string; name?: string },
            { ok: true; id: string; address: string }
        >(fns, 'adminAddAccount');
        const res = await fn({ account_id: accountId, name });
        return res.data;
    },

    async adminDeleteUser(uid: Identifier) {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<{ uid: string }, { ok: true }>(
            fns,
            'adminDeleteUser'
        );
        const res = await fn({ uid: String(uid) });
        return res.data;
    },

    async acceptInvite(newPassword: string) {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<{ newPassword: string }, { ok: true }>(
            fns,
            'acceptInvite'
        );
        const res = await fn({ newPassword });
        return res.data;
    },

    // One call per decision in the legal dialog. An empty list only makes the
    // server recompute `legal.valid_until`, which repairs a user record that
    // lacks the field. Times in the answer are milliseconds.
    async recordLegalDecisions(
        decisions: LegalDecision[]
    ): Promise<RecordLegalDecisionsResult> {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<
            { decisions: LegalDecision[] },
            RecordLegalDecisionsResult
        >(fns, 'recordLegalDecisions');
        const res = await fn({ decisions });
        return res.data;
    },

    async updateMyProfile(patch: { first_name?: string; last_name?: string }) {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<
            { patch: { first_name?: string; last_name?: string } },
            { ok: true }
        >(fns, 'updateMyProfile');
        const res = await fn({ patch });
        return res.data;
    },

    async cleanupExpiredInvites() {
        const fns = getFunctions(firebase.app(), 'europe-west6');
        const fn = httpsCallable<
            Record<string, never>,
            { ok: true; cleaned: number }
        >(fns, 'cleanupExpiredInvites');
        const res = await fn({});
        return res.data;
    },

    async getActivityLog(companyId?: Identifier) {
        return getActivityLog(baseDataProvider, companyId);
    },
} satisfies DataProvider;

export type IrmDataProvider = typeof dataProviderWithCustomMethods;

// Apply Lifecycle Callbacks for File Uploads
export const dataProvider = withLifecycleCallbacks(
    dataProviderWithCustomMethods,
    [
        {
            resource: 'contactNotes',
            beforeSave: async (data: ContactNote) => {
                if (data.attachments) {
                    for (const fi of data.attachments) {
                        await uploadToBucket(fi);
                    }
                }
                return data;
            },
        },
        {
            resource: 'dealNotes',
            beforeSave: async (data: DealNote) => {
                if (data.attachments) {
                    for (const fi of data.attachments) {
                        await uploadToBucket(fi);
                    }
                }
                return data;
            },
        },
        {
            resource: 'users',
            beforeSave: async (data: User) => {
                /*
            if (data.avatar) {
                await uploadToBucket(data.avatar);
            }
            */
                return data;
            },
        },
        {
            resource: 'contacts',
            beforeCreate: async params => processContactAvatar(params),
            beforeUpdate: async params => processContactAvatar(params),
        },
        {
            resource: 'companies',
            beforeCreate: async params => processCompanyLogo(params),
            beforeUpdate: async params => processCompanyLogo(params),
        },
    ]
);

// File Upload Handling with Firebase Storage
const uploadToBucket = async (fi: RAFile) => {
    if (!fi.src.startsWith('blob:') && !fi.src.startsWith('data:')) {
        if (fi.path) {
            const storageRef = firebase.storage().ref();
            const fileRef = storageRef.child(`attachments/${fi.path}`);
            try {
                await fileRef.getDownloadURL();
                return;
            } catch (error) {
                // Continue if file doesn't exist
            }
        }
    }

    const dataContent = fi.src
        ? await fetch(fi.src).then(res => res.blob())
        : fi.rawFile;

    const file = fi.rawFile;
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `attachments/${fileName}`;

    const storageRef = firebase.storage().ref();
    const fileRef = storageRef.child(filePath);
    await fileRef.put(dataContent);
    const fileUrl = await fileRef.getDownloadURL();

    fi.path = filePath;
    fi.src = fileUrl;

    return fi;
};
